import { useEffect, useState } from "react";
import { Apple, Monitor, Download, ShieldCheck, Terminal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, API } from "@/lib/api";
import { UnsignedHelp } from "@/components/UnsignedHelp";

const Card = ({ icon: Icon, title, children }) => (
  <div className="rounded-lg border border-slate-200 p-5">
    <div className="flex items-center gap-2 mb-3"><Icon className="h-5 w-5 text-blue-600" /><h3 className="font-semibold">{title}</h3></div>
    <div className="space-y-3 text-sm text-slate-600">{children}</div>
  </div>
);

export const InstallOptions = () => {
  const [key, setKey] = useState("");
  const [binaries, setBinaries] = useState(false);

  useEffect(() => {
    api.get("/settings/enrollment").then(({ data }) => setKey(data.key));
    api.get("/agent/meta").then(({ data }) => setBinaries(data.binaries));
  }, []);

  const url = (kind) => `${API}/agent/installer/${kind}?key=${encodeURIComponent(key)}`;

  if (!binaries) {
    return (
      <p className="text-sm bg-amber-50 text-amber-800 rounded-lg p-4" data-testid="exe-pending-note">
        Les installateurs sont en cours de préparation. Relancez la compilation GitHub, puis revenez ici : les deux fichiers apparaîtront automatiquement.
      </p>
    );
  }

  return (
    <div className="space-y-5" data-testid="install-options">
      <p className="text-sm text-slate-600">Un seul fichier par système. Le client le télécharge, double-clique, et c'est tout — aucune ligne de commande.</p>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card icon={Monitor} title="Windows">
          <Button asChild className="w-full h-11"><a href={url("windows-exe")} data-testid="download-windows-exe">
            <Download className="h-4 w-4 mr-2" /> Télécharger ScreenView.exe</a></Button>
          <p>Le client double-clique sur le fichier et accepte la fenêtre bleue de confirmation Windows. L'agent s'installe alors tout seul et démarre avec l'ordinateur, même sur l'écran de connexion.</p>
          <UnsignedHelp os="windows" />
        </Card>
        <Card icon={Apple} title="macOS">
          <Button asChild className="w-full h-11"><a href={url("mac-pkg")} data-testid="download-mac-pkg">
            <Download className="h-4 w-4 mr-2" /> Télécharger ScreenView.pkg</a></Button>
          <p>Le client double-clique sur le fichier et suit l'installation. Au premier lancement, macOS demande d'autoriser « Enregistrement de l'écran » et « Accessibilité » : une case à cocher, sans ligne de commande.</p>
          <UnsignedHelp os="mac" />
        </Card>
        <Card icon={Terminal} title="Linux">
          <Button asChild className="w-full h-11"><a href={url("linux-sh")} data-testid="download-linux-sh">
            <Download className="h-4 w-4 mr-2" /> Télécharger l'installateur Linux</a></Button>
          <p>Déposez le fichier sur le poste Linux puis lancez <code className="bg-slate-100 px-1 rounded">bash Installer-ScreenView.sh</code> dans un terminal. L'agent démarre automatiquement (service utilisateur systemd). Session graphique X11 requise.</p>
        </Card>
      </div>
      <p className="flex items-center gap-2 text-xs text-slate-500" data-testid="install-signing-note">
        <ShieldCheck className="h-4 w-4 text-slate-400" />
        Ces avertissements disparaîtront dès que les fichiers seront signés avec vos certificats Windows et Apple.
      </p>
    </div>
  );
};
